import React, { useState, useEffect, useCallback } from 'react';
import { settingsAPI, workforceAPI, notificationsAPI } from '../lib/api';
import { formatINR, formatDate, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Save,
  Loader2,
  Calculator,
  Landmark,
  CheckCircle2,
  XCircle,
  Send,
  Eye,
  Activity as ActivityIcon,
} from 'lucide-react';

const EMPTY_ENTITY = () => ({
  legal_name: '',
  short_name: '',
  address_line: '',
  city_state_pincode: '',
  country: 'India',
  gstin: '',
  msme: '',
  state_code: '',
  signatory_name: '',
  signatory_role: 'Director',
  invoice_prefix: 'INV',
  next_invoice_seq: 1,
  invoice_number_width: 6,
  phone: '',
  email: '',
  website: '',
  contact_name: '',
});

// Zero-pad width of the invoice sequence: blank/invalid falls back to the server default (6),
// a real 0 is kept (plain numbers); capped so a typo can't build a giant string.
const padWidth = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 12) : 6;
};

export default function Settings() {
  const { can } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    address: '',
    phone: '',
    email: '',
    tax_id: '',
    monthly_overhead: 0,
    working_days_per_month: 30,
    min_margin_threshold: 20,
    target_margin: 30,
    partnership: EMPTY_ENTITY(),
    // `workforce` only arrives for admins (GET /settings strips it otherwise).
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingWf, setTestingWf] = useState(false);
  const [savingWf, setSavingWf] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const res = await settingsAPI.get();
      setSettings({
        ...res.data,
        partnership: { ...EMPTY_ENTITY(), ...(res.data.partnership || {}) },
      });
    } catch (err) {
      toast.error(apiError(err, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateEntity = (key, field, value) => {
    setSettings((s) => ({
      ...s,
      [key]: { ...s[key], [field]: value },
    }));
  };

  // One payload for both save buttons: numbers coerced so an emptied field
  // can't 422 the whole save. `num` keeps a real 0 (|| would turn 0 into the default).
  const num = (v, d) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : d);
  const buildPayload = () => ({
    ...settings,
    monthly_overhead: num(settings.monthly_overhead, 0),
    working_days_per_month: parseInt(settings.working_days_per_month) || 30,
    min_margin_threshold: num(settings.min_margin_threshold, 20),
    target_margin: num(settings.target_margin, 30),
    // PUT replaces the whole block: spread everything loaded (incl. keys this page
    // doesn't edit), then override only the coerced numbers.
    partnership: {
      ...settings.partnership,
      next_invoice_seq: parseInt(settings.partnership.next_invoice_seq) || 1,
      invoice_number_width: padWidth(settings.partnership.invoice_number_width),
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.update(buildPayload());
      toast.success('Settings saved');
    } catch (err) {
      toast.error(apiError(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const testWorkforce = async () => {
    const { base_url, read_key } = settings.workforce || {};
    if (!base_url || !read_key) return toast.error('Enter the Base URL and Read Key first');
    setTestingWf(true);
    try {
      const res = await workforceAPI.test(base_url, read_key);
      toast.success(`Connected — ${res.data.machines} machine${res.data.machines === 1 ? '' : 's'} seen`);
    } catch (err) {
      toast.error(apiError(err, 'Could not reach the ingest service'));
    } finally {
      setTestingWf(false);
    }
  };

  const saveWorkforce = async () => {
    setSavingWf(true);
    try {
      await settingsAPI.update(buildPayload());
      toast.success('Workforce settings saved');
    } catch (err) {
      toast.error(apiError(err, 'Failed to save'));
    } finally {
      setSavingWf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Settings</h1>
        <p className="text-gray-500 mt-1">Company, letterhead, costing, and invoice delivery</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* ====== Global company info ====== */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Company</CardTitle>
                <CardDescription>Top-level display info used across the app</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="set-f1">Display name</Label>
                <Input id="set-f1"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="DESENO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="set-f2">Tax ID (legacy)</Label>
                <Input id="set-f2"
                  value={settings.tax_id}
                  onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                  placeholder="GSTIN"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="set-f3">Address</Label>
              <Input id="set-f3"
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="set-f4">Phone</Label>
                <Input id="set-f4"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="set-f5">Email</Label>
                <Input id="set-f5"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ====== DESENO letterhead ====== */}
        <EntityCard
          title="DESENO (Partnership)"
          subtitle="Letterhead, invoice numbering and signatory used on every invoice, statement and proposal"
          entity={settings.partnership}
          onChange={(field, value) => updateEntity('partnership', field, value)}
        />

        {/* ====== Overhead + margin (legacy, unchanged) ====== */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Overhead & margin</CardTitle>
                <CardDescription>Used for proposal cost calculations</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="set-f6">Monthly overhead (₹)</Label>
                <Input id="set-f6"
                  type="number"
                  value={settings.monthly_overhead}
                  onChange={(e) => setSettings({ ...settings, monthly_overhead: e.target.value })}
                />
                <p className="text-xs text-gray-500">Total fixed costs: rent, utilities, software, etc.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="set-f7">Working days per month</Label>
                <Input id="set-f7"
                  type="number"
                  min="20"
                  max="31"
                  value={settings.working_days_per_month}
                  onChange={(e) => setSettings({ ...settings, working_days_per_month: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="set-f8">Min margin threshold (%)</Label>
                <Input id="set-f8"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.min_margin_threshold}
                  onChange={(e) => setSettings({ ...settings, min_margin_threshold: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="set-f9">Target margin (%)</Label>
                <Input id="set-f9"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.target_margin}
                  onChange={(e) => setSettings({ ...settings, target_margin: e.target.value })}
                />
              </div>
            </div>
            {settings.monthly_overhead > 0 && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs text-gray-500">Daily overhead rate</p>
                <p className="text-lg font-bold font-mono">
                  {formatINR(settings.monthly_overhead / (settings.working_days_per_month || 30))}
                </p>
              </div>
            )}
            <div className="p-3 bg-gray-50 rounded-md flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-600" /> Below {settings.min_margin_threshold}%</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-500" /> {settings.min_margin_threshold}–{settings.target_margin}%</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-600" /> {settings.target_margin}%+</div>
            </div>
          </CardContent>
        </Card>

        {/* ====== Save bar (sticky-ish) ====== */}
        <div className="flex justify-end">
          <Button type="submit" className="bg-black hover:bg-black/90" disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
        </div>
      </form>

      {/* ====== Invoice delivery + payment reminders ====== */}
      <NotificationsCard />

      {/* ====== Workforce monitor integration (admins only) ====== */}
      {can('workforce') && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <ActivityIcon className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Workforce Monitor</CardTitle>
                <CardDescription>
                  The VPS ingest service that collects desktop-agent activity. PriceIntel reads daily aggregates from it — keys never reach the browser dashboards.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wf-url">Ingest Base URL</Label>
                <Input
                  id="wf-url"
                  placeholder="https://workforce.deseno.co.in"
                  value={settings.workforce?.base_url || ''}
                  onChange={(e) => setSettings((s) => ({ ...s, workforce: { ...(s.workforce || {}), base_url: e.target.value } }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wf-key">Read Key</Label>
                <Input
                  id="wf-key"
                  type="password"
                  placeholder="X-Read-Key value from the ingest .env"
                  value={settings.workforce?.read_key || ''}
                  onChange={(e) => setSettings((s) => ({ ...s, workforce: { ...(s.workforce || {}), read_key: e.target.value } }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={testWorkforce} disabled={testingWf}>
                {testingWf ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Test Connection
              </Button>
              <Button type="button" className="bg-black hover:bg-black/90" onClick={saveWorkforce} disabled={savingWf}>
                {savingWf ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function EntityCard({ title, subtitle, entity, onChange }) {
  const nextNumber = `${entity.invoice_prefix ?? ''}${String(parseInt(entity.next_invoice_seq, 10) || 1).padStart(padWidth(entity.invoice_number_width), '0')}`;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set-f10">Legal name (appears on invoices)</Label>
            <Input id="set-f10"
              value={entity.legal_name}
              onChange={(e) => onChange('legal_name', e.target.value)}
              placeholder="DESENO"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f11">Short name (display label)</Label>
            <Input id="set-f11"
              value={entity.short_name}
              onChange={(e) => onChange('short_name', e.target.value)}
              placeholder="DESENO"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="set-f12">Address line</Label>
          <Input id="set-f12"
            value={entity.address_line}
            onChange={(e) => onChange('address_line', e.target.value)}
            placeholder="Office No. 201, 3rd floor, Krishna Enclave…"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set-f13">City, State, Pincode</Label>
            <Input id="set-f13"
              value={entity.city_state_pincode}
              onChange={(e) => onChange('city_state_pincode', e.target.value)}
              placeholder="Nashik Maharashtra 422005"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f14">Country</Label>
            <Input id="set-f14"
              value={entity.country}
              onChange={(e) => onChange('country', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set-f15">GSTIN</Label>
            <Input id="set-f15"
              value={entity.gstin}
              onChange={(e) => onChange('gstin', e.target.value.toUpperCase())}
              placeholder="Leave blank if not GST-registered"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f16">MSME number</Label>
            <Input id="set-f16"
              value={entity.msme}
              onChange={(e) => onChange('msme', e.target.value)}
              placeholder="UDYAM-XX-00-0000000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f17">State code</Label>
            <Input id="set-f17"
              value={entity.state_code}
              onChange={(e) => onChange('state_code', e.target.value)}
              placeholder="27"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set-f18">Signatory name</Label>
            <Input id="set-f18"
              value={entity.signatory_name}
              onChange={(e) => onChange('signatory_name', e.target.value)}
              placeholder="Murtaza Udaypurwala"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f19">Signatory role</Label>
            <Input id="set-f19"
              value={entity.signatory_role}
              onChange={(e) => onChange('signatory_role', e.target.value)}
              placeholder="Partner"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ent-phone">Phone</Label>
            <Input
              id="ent-phone"
              data-testid="entity-phone"
              value={entity.phone ?? ''}
              onChange={(e) => onChange('phone', e.target.value)}
              placeholder="+91 98765 43210"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-email">Email</Label>
            <Input
              id="ent-email"
              data-testid="entity-email"
              type="email"
              value={entity.email ?? ''}
              onChange={(e) => onChange('email', e.target.value)}
              placeholder="accounts@deseno.co.in"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-website">Website</Label>
            <Input
              id="ent-website"
              data-testid="entity-website"
              value={entity.website ?? ''}
              onChange={(e) => onChange('website', e.target.value)}
              placeholder="www.deseno.co.in"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-contact">Contact name printed on invoices</Label>
            <Input
              id="ent-contact"
              data-testid="entity-contact-name"
              value={entity.contact_name ?? ''}
              onChange={(e) => onChange('contact_name', e.target.value)}
              placeholder="Murtaza Udaypurwala"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set-f20">Invoice number prefix</Label>
            <Input id="set-f20"
              value={entity.invoice_prefix}
              onChange={(e) => onChange('invoice_prefix', e.target.value)}
              placeholder="INVFY25/26"
            />
            <p className="text-xs text-gray-500">Appears before the running sequence number.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="set-f21">Next invoice sequence #</Label>
            <Input id="set-f21"
              type="number"
              min="1"
              value={entity.next_invoice_seq}
              onChange={(e) => onChange('next_invoice_seq', e.target.value)}
            />
            <p className="text-xs text-gray-500">The next invoice issued from PriceIntel will use this number.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-width">Invoice number width (0 = no zero padding)</Label>
            <Input
              id="ent-width"
              data-testid="entity-invoice-width"
              type="number"
              min="0"
              max="12"
              value={entity.invoice_number_width ?? ''}
              onChange={(e) => onChange('invoice_number_width', e.target.value)}
            />
          </div>
        </div>
        <div className="p-3 bg-gray-50 rounded-md text-sm" data-testid="entity-invoice-preview">
          Next invoice number: <span className="font-bold font-mono break-all">{nextNumber}</span>
        </div>
      </CardContent>
    </Card>
  );
}


/* ================= INVOICE DELIVERY + REMINDERS ================= */

const REMINDER_HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM – 8 PM IST
const hourLabel = (h) => `${h % 12 || 12}:00 ${h < 12 ? 'AM' : 'PM'}`;
const CHANNEL_LABEL = { email: 'Email', whatsapp: 'WhatsApp' };
const dueLabel = (d) => (d === 0 ? 'Due today' : d < 0 ? `${-d}d before due` : `${d}d overdue`);
const whenLabel = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' })
  : '');
const SEND_STATUS_CLASS = {
  sent: 'bg-black text-white',
  failed: 'border border-black text-black',
};

// Auto reminders log step "d-3" / "d0" / "d7"; manual ones "manual:<uuid>".
function kindLabel(n) {
  if (n.kind === 'invoice') return 'Invoice';
  const m = /^d(-?\d+)$/.exec(n.step || '');
  return m ? `Reminder · ${dueLabel(Number(m[1]))}` : 'Reminder (manual)';
}

// status: null = still checking, false = the check failed
function StatusLine({ status, ok, okText, badText }) {
  if (status === null) {
    return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>;
  }
  if (status === false) {
    return <div className="flex items-center gap-2 text-gray-500"><XCircle className="w-4 h-4" /> Couldn't check right now</div>;
  }
  return ok
    ? <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="w-4 h-4" /> {okText}</div>
    : <div className="flex items-center gap-2 text-gray-600"><XCircle className="w-4 h-4 text-gray-400" /> {badText}</div>;
}

function NotificationsCard() {
  const { can } = useAuth();
  const [status, setStatus] = useState(null);
  const [conf, setConf] = useState({ enabled: false, channels: 'both', send_hour_ist: 10, email_bcc: '' });
  const [testTo, setTestTo] = useState({ email: '', whatsapp: '' });
  const [testing, setTesting] = useState('');
  const [recent, setRecent] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, c, r] = await Promise.allSettled([
        notificationsAPI.status(),
        notificationsAPI.getSettings(),
        notificationsAPI.recent(20),
      ]);
      setStatus(s.status === 'fulfilled' ? s.value.data : false);
      if (c.status === 'fulfilled') setConf(c.value.data);
      if (r.status === 'fulfilled') setRecent(r.value.data || []);
      const failed = [s, c, r].find((x) => x.status === 'rejected');
      if (failed) toast.error(apiError(failed.reason, 'Could not load email & WhatsApp settings'));
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await notificationsAPI.updateSettings({
        enabled: !!conf.enabled,
        channels: conf.channels,
        send_hour_ist: Number(conf.send_hour_ist),
        email_bcc: (conf.email_bcc || '').trim(),
      });
      setConf(res.data);
      setPreview(null); // the preview reflects saved settings
      toast.success(res.data.enabled ? 'Saved. Automatic reminders are on' : 'Saved. Automatic reminders are off');
    } catch (err) {
      toast.error(apiError(err, 'Failed to save reminder settings'));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (channel) => {
    const to = (testTo[channel] || '').trim();
    if (!to) return toast.error(channel === 'email' ? 'Type an email address first' : 'Type a mobile number first');
    setTesting(channel);
    try {
      const res = await notificationsAPI.sendTest(channel, to);
      toast.success(`Test ${channel === 'email' ? 'email' : 'WhatsApp message'} accepted for ${res.data.to}. Check that it arrived.`);
    } catch (err) {
      toast.error(apiError(err, 'The test message failed'));
    } finally {
      setTesting('');
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await notificationsAPI.preview();
      setPreview(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Could not build the preview'));
    } finally {
      setPreviewing(false);
    }
  };

  const wa = status?.whatsapp;
  const items = preview?.items || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Send className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-lg">Invoices by email & WhatsApp</CardTitle>
            <CardDescription>
              Email goes out through Resend, WhatsApp through the WhatsApp Business API. Both attach the invoice PDF.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-gray-50 rounded-md space-y-1">
            <div className="text-gray-500 text-xs uppercase tracking-wide">Email</div>
            <StatusLine
              status={status}
              ok={status?.email?.configured}
              okText="Ready"
              badText="Not set up: add the Resend API key on the server"
            />
            {status?.email?.from && (
              <div className="text-xs text-gray-500 break-all">From: {status.email.from}</div>
            )}
          </div>
          <div className="p-3 bg-gray-50 rounded-md space-y-1">
            <div className="text-gray-500 text-xs uppercase tracking-wide">WhatsApp</div>
            <StatusLine
              status={status}
              ok={wa?.configured && wa?.ok !== false}
              okText={`Connected${wa?.phone ? ` · ${wa.phone}` : ''}`}
              badText={wa?.configured
                ? `Meta rejected the WhatsApp token or couldn't be reached: ${wa.error || 'unknown error'}. Update WHATSAPP_TOKEN on the server.`
                : 'Not set up: add the WhatsApp token on the server'}
            />
            {wa?.configured && (
              <div className="text-xs text-gray-500 space-y-0.5">
                <div>Invoice PDF template: <span className="font-medium text-gray-800">{wa.invoice_template || 'Not found'}</span></div>
                <div>Payment reminder template: <span className="font-medium text-gray-800">{wa.reminder_template || 'Not found'}</span></div>
                {wa.invoice_template !== 'APPROVED' && (
                  <p className="pt-1">Invoices go out on WhatsApp once Meta approves the template. Email works meanwhile.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Blind copy of every invoice email */}
        <div className="border-t pt-4 space-y-2">
          <Label htmlFor="email-bcc">Copy every invoice email to (optional)</Label>
          <Input
            id="email-bcc"
            value={conf.email_bcc || ''}
            onChange={(e) => setConf({ ...conf, email_bcc: e.target.value })}
            placeholder="e.g. accounts@deseno.co.in (blind copy, clients don't see it)"
            data-testid="email-bcc"
          />
          <p className="text-xs text-gray-500">So the team can see exactly what went out. Saved with the button below.</p>
        </div>

        {/* Self-test (admin) */}
        {can('users') && (
          <div className="border-t pt-4 space-y-3" data-testid="notify-test">
            <p className="text-sm font-medium">Send a test message</p>
            <p className="text-xs text-gray-500">Checks that the connection works end to end. Use your own address and number, not a client's.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex gap-2">
                <Input value={testTo.email} onChange={(e) => setTestTo({ ...testTo, email: e.target.value })} placeholder="you@deseno.co.in" data-testid="test-email-to" />
                <Button type="button" variant="outline" onClick={() => sendTest('email')} disabled={!!testing} data-testid="test-email-btn">
                  {testing === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test email'}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input type="tel" value={testTo.whatsapp} onChange={(e) => setTestTo({ ...testTo, whatsapp: e.target.value })} placeholder="+91 98xxx xxxxx" data-testid="test-whatsapp-to" />
                <Button type="button" variant="outline" onClick={() => sendTest('whatsapp')} disabled={!!testing} data-testid="test-whatsapp-btn">
                  {testing === 'whatsapp' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test WhatsApp'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Automatic reminders */}
        <div className="border-t pt-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Automatic payment reminders</p>
              <p className="text-xs text-gray-500 mt-1">
                3 days before due, on the due date, then 3, 7 and 15 days after, only while a balance is unpaid;
                clients with reminders paused are skipped.
              </p>
            </div>
            <Switch
              checked={!!conf.enabled}
              onCheckedChange={(v) => setConf({ ...conf, enabled: v })}
              aria-label="Automatic payment reminders"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rem-channels">Send by</Label>
              <Select value={conf.channels} onValueChange={(v) => setConf({ ...conf, channels: v })}>
                <SelectTrigger id="rem-channels"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">WhatsApp + Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                  <SelectItem value="email">Email only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rem-hour">Send from (IST)</Label>
              <Select
                value={String(conf.send_hour_ist)}
                onValueChange={(v) => setConf({ ...conf, send_hour_ist: Number(v) })}
              >
                <SelectTrigger id="rem-hour"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REMINDER_HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="bg-black hover:bg-black/90" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save notification settings
            </Button>
            <Button type="button" variant="outline" onClick={runPreview} disabled={previewing}>
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              Preview today
            </Button>
          </div>

          {preview && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                {items.length === 0
                  ? `No reminders fall due on ${formatDate(preview.today)}.`
                  : `Reminders for ${formatDate(preview.today)}, using the saved settings.`}
                {!preview.settings?.enabled && ' Automatic reminders are off, so nothing will actually be sent.'}
              </p>
              {items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <tr>
                        <th className="py-2 pr-3">Invoice</th>
                        <th className="py-2 pr-3">Client</th>
                        <th className="py-2 pr-3">Due</th>
                        <th className="py-2 pr-3">Days</th>
                        <th className="py-2 pr-3">Channel</th>
                        <th className="py-2 pr-3">To</th>
                        <th className="py-2 pr-3">Will send</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={`${it.invoice_id}-${it.channel}`} className="border-b last:border-0">
                          <td className="py-2 pr-3 font-mono whitespace-nowrap">{it.invoice_number}</td>
                          <td className="py-2 pr-3 truncate max-w-[180px]">{it.client_name}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{formatDate(it.due_at)}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{dueLabel(it.days_from_due)}</td>
                          <td className="py-2 pr-3">{CHANNEL_LABEL[it.channel] || it.channel}</td>
                          <td className="py-2 pr-3 text-gray-600 truncate max-w-[200px]">{it.to || '—'}</td>
                          <td className="py-2 pr-3">
                            {it.will_send
                              ? <span className="font-medium">Yes</span>
                              : <span className="text-gray-500">No · {it.reason}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="border-t pt-4 space-y-2">
          <p className="text-sm font-medium">Recent activity</p>
          {recent.length === 0 ? (
            <p className="text-xs text-gray-500">Nothing sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Invoice</th>
                    <th className="py-2 pr-3">Client</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Channel</th>
                    <th className="py-2 pr-3">To</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((n) => (
                    <tr key={n.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <div className="text-gray-700">{whenLabel(n.created_at)}</div>
                        <div className="text-xs text-gray-400">{n.created_by === 'auto' ? 'Automatic' : n.created_by}</div>
                      </td>
                      <td className="py-2 pr-3 font-mono whitespace-nowrap">{n.invoice_number}</td>
                      <td className="py-2 pr-3 truncate max-w-[180px]">{n.client_name}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{kindLabel(n)}</td>
                      <td className="py-2 pr-3">{CHANNEL_LABEL[n.channel] || n.channel}</td>
                      <td className="py-2 pr-3 text-gray-600 truncate max-w-[200px]">{n.to || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${SEND_STATUS_CLASS[n.status] || 'bg-gray-100 text-gray-600'}`}>
                          {n.status}
                        </span>
                        {n.error && <div className="text-xs text-gray-500 mt-0.5 min-w-[160px] max-w-[260px]">{n.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
